import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { Play, Volume2, VolumeX } from "lucide-react";
import { PORTFOLIO_PROJECTS } from "../../lib/data";

function VideoCard({ project, index }) {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);

  const togglePlay = async () => {
    const video = videoRef.current;
    if (!video) return;

    if (video.paused) {
      try {
        await video.play();
        setPlaying(true);
      } catch {
        setPlaying(false);
      }
    } else {
      video.pause();
      setPlaying(false);
    }
  };

  const toggleMute = (event) => {
    event.stopPropagation();
    const video = videoRef.current;
    if (!video) return;
    video.muted = !video.muted;
    setMuted(video.muted);
  };

  return (
    <motion.article
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.7, delay: index * 0.05, ease: [0.65, 0, 0.35, 1] }}
      className="group"
    >
      <div
        className="relative aspect-[9/16] w-full cursor-pointer overflow-hidden rounded-2xl border border-white/10 bg-[#090909] shadow-2xl md:rounded-[1.35rem]"
        onClick={togglePlay}
        role="button"
        tabIndex={0}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            togglePlay();
          }
        }}
        aria-label={`${playing ? "Пауза" : "Воспроизвести"}: ${project.title}`}
      >
        <video
          ref={videoRef}
          src={project.video}
          poster={project.image}
          className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.02]"
          playsInline
          preload="metadata"
          muted={muted}
          loop
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
        />

        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/85 via-black/5 to-black/10" />

        {!playing && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full border border-white/30 bg-black/35 backdrop-blur-md transition-transform duration-300 group-hover:scale-110 md:h-20 md:w-20">
              <Play className="ml-1 h-6 w-6 fill-white text-white md:h-7 md:w-7" aria-hidden />
            </div>
          </div>
        )}

        <div className="absolute left-4 top-4 flex items-center gap-2 rounded-full border border-white/15 bg-black/35 px-3 py-2 backdrop-blur-md">
          <span className="font-editorial text-sm italic text-white/70">{project.id}</span>
          <span className="h-1 w-1 rounded-full bg-[#e63946]" />
          <span className="font-body text-[9px] tracking-[0.2em] text-white uppercase">{project.category}</span>
        </div>

        <button
          type="button"
          onClick={toggleMute}
          aria-label={muted ? "Включить звук" : "Выключить звук"}
          className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-full border border-white/15 bg-black/35 text-white backdrop-blur-md transition-colors hover:bg-black/60"
        >
          {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
        </button>

        <div className="absolute inset-x-4 bottom-4">
          <h3 className="font-display text-3xl uppercase leading-none text-white sm:text-4xl">
            {project.title}
          </h3>
          {project.subtitle && (
            <p className="mt-2 font-editorial text-sm italic text-white/60">
              {project.subtitle}
            </p>
          )}
          <p className="mt-3 max-w-[90%] font-body text-xs leading-relaxed text-white/60">
            {project.description}
          </p>
        </div>
      </div>

      <div className="mt-4 flex items-center justify-between px-1">
        <span className="font-body text-[9px] tracking-[0.22em] text-white/35 uppercase">
          Нажмите на видео
        </span>
        <span className="font-body text-[9px] tracking-[0.22em] text-[#e63946] uppercase">
          9:16
        </span>
      </div>
    </motion.article>
  );
}

export default function Portfolio() {
  return (
    <section
      id="portfolio"
      data-testid="portfolio-section"
      className="relative px-5 py-20 md:px-12 md:py-32"
    >
      <div className="mx-auto max-w-[1400px]">
        <div className="mb-12 flex flex-col gap-6 md:mb-20 md:flex-row md:items-end md:justify-between">
          <div>
            <span className="section-eyebrow">Глава 02 — Работы</span>
            <h2 className="mt-4 font-display text-6xl uppercase leading-[0.86] tracking-tight text-white sm:text-7xl md:text-8xl lg:text-[8.5rem]">
              Избранные
              <br />
              <span className="text-white/20">работы</span>
            </h2>
          </div>
          <p className="max-w-sm font-body text-sm leading-relaxed text-white/55 md:text-base">
            Вертикальные видео для брендов, бизнеса и социальных сетей.
            Нажмите прямо на видео, чтобы воспроизвести.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3 lg:gap-10">
          {PORTFOLIO_PROJECTS.map((project, index) => (
            <VideoCard key={project.id} project={project} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
}
